import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoogleAuthDebug } from '../components/Auth/GoogleAuthDebug';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
/**
 * Google Auth Test Page
 * Use this page to test Google OAuth integration
 * Add to AppRouter.tsx: <Route path="/test-google" element={<GoogleAuthTestPage />} />
 */
export function GoogleAuthTestPage() {
  const {
    user,
    signInWithGoogle,
    signOut
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign-in error:', error);
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    await signOut();
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-4151">
      <header className="bg-white border-b border-gray-200" data-id="element-4152">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-4153">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-4154">
            <ArrowLeft size={16} className="mr-1" data-id="element-4155" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-4156">
        <div className="w-full max-w-2xl" data-id="element-4157">
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6" data-id="element-4158">
            <h1 className="text-3xl font-bold mb-2" data-id="element-4159">
              🧪 Google OAuth Test Page
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-4160">
              Use this page to test your Google Sign-In integration
            </p>

            {/* Current Status */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-4161">
              <h2 className="font-semibold mb-3" data-id="element-4162">Current Status</h2>
              <div className="space-y-2 text-sm" data-id="element-4163">
                <div className="flex items-center justify-between" data-id="element-4164">
                  <span data-id="element-4165">Authentication:</span>
                  <span className={`font-medium ${user ? 'text-green-600' : 'text-red-600'}`} data-id="element-4166">
                    {user ? '✅ Logged In' : '❌ Not Logged In'}
                  </span>
                </div>
                {user && <>
                    <div className="flex items-center justify-between" data-id="element-4167">
                      <span data-id="element-4168">Email:</span>
                      <span className="font-medium" data-id="element-4169">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4170">
                      <span data-id="element-4171">Name:</span>
                      <span className="font-medium" data-id="element-4172">
                        {user.user_metadata?.fullName || 'Not set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4173">
                      <span data-id="element-4174">Provider:</span>
                      <span className="font-medium" data-id="element-4175">
                        {user.app_metadata?.provider || 'Unknown'}
                      </span>
                    </div>
                  </>}
              </div>
            </div>

            {/* Test Actions */}
            <div className="space-y-4" data-id="element-4176">
              {!user ? <>
                  <h2 className="font-semibold mb-3" data-id="element-4177">Test Google Sign-In</h2>
                  <GoogleSignInButton onClick={handleGoogleSignIn} loading={loading} text="Test Google Sign-In" data-id="element-4178" />
                  <p className="text-xs text-gray-500 text-center" data-id="element-4179">
                    Click the button above to test the Google OAuth flow
                  </p>
                </> : <>
                  <h2 className="font-semibold mb-3" data-id="element-4180">
                    Logged In Successfully!
                  </h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-4181">
                    <p className="text-sm text-green-800" data-id="element-4182">
                      ✅ Google Sign-In is working correctly!
                    </p>
                  </div>
                  <button onClick={handleSignOut} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-4183">
                    Sign Out
                  </button>
                </>}
            </div>

            {/* Setup Checklist */}
            <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-4184">
              <h2 className="font-semibold mb-3" data-id="element-4185">Setup Checklist</h2>
              <div className="space-y-2 text-sm" data-id="element-4186">
                <label className="flex items-start" data-id="element-4187">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4188" />
                  <span data-id="element-4189">Enabled Google provider in Supabase Dashboard</span>
                </label>
                <label className="flex items-start" data-id="element-4190">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4191" />
                  <span data-id="element-4192">Added OAuth Client ID and Secret in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4193">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4194" />
                  <span data-id="element-4195">Verified redirect URL in Google Cloud Console</span>
                </label>
                <label className="flex items-start" data-id="element-4196">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4197" />
                  <span data-id="element-4198">Disabled "Skip nonce checks" in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4199">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4200" />
                  <span data-id="element-4201">Disabled "Allow users without email" in Supabase</span>
                </label>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4202">
              <h2 className="font-semibold mb-3" data-id="element-4203">Quick Links</h2>
              <div className="space-y-2" data-id="element-4204">
                <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4205">
                  → Supabase Auth Providers Dashboard
                </a>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4206">
                  → Google Cloud Console Credentials
                </a>
                <Link to="/signup" className="block text-sm text-purple-600 hover:underline" data-id="element-4207">
                  → Go to Signup Page
                </Link>
                <Link to="/login" className="block text-sm text-purple-600 hover:underline" data-id="element-4208">
                  → Go to Login Page
                </Link>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-4209">
            <h3 className="font-semibold text-blue-900 mb-3" data-id="element-4210">
              📋 Testing Instructions
            </h3>
            <ol className="space-y-2 text-sm text-blue-800" data-id="element-4211">
              <li data-id="element-4212">1. Complete the setup checklist above</li>
              <li data-id="element-4213">2. Click "Test Google Sign-In" button</li>
              <li data-id="element-4214">3. You'll be redirected to Google OAuth</li>
              <li data-id="element-4215">4. Authorize the app</li>
              <li data-id="element-4216">5. You'll be redirected back (to /onboarding)</li>
              <li data-id="element-4217">6. Check that you're logged in</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      <GoogleAuthDebug data-id="element-4218" />
    </div>;
}